/**
 * @fileoverview Connection request and user report handlers for chat.
 * @module logic/chat/chatConnectionHandlers
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { refreshToken, signOut } from "../../utils/common.js";
import { getGenericErrorMessage } from "../../utils/genericErrorMessages.js";
import { getCompanyByRFC } from "../company/index.js";
import { getUser } from "../user/index.js";
import {
  acceptConnectionRequest,
  rejectConnectionRequest,
  updateConnectionRequest,
} from "../connection/index.js";
import {
  updateAppointment,
  createVirtualAppointment,
} from "../appointment/index.js";
import { fetchUserChats } from "./chatApi.js";
import {
  COMPANY_NOT_FOUND,
  HTTP_CREATED,
  HTTP_OK,
} from "./chatConstants.js";

const navigateAfterCustomerCompanyConnectionAccepted = (
  connectionRequest,
  navigate,
) => {
  if (connectionRequest.type !== "customerCompanyUserConnectionRequest") {
    return;
  }

  const {
    businessEventId,
    redirectToRegister,
    participatingCompanyId,
    businessEventRequirementId,
  } = connectionRequest;

  if (
    redirectToRegister === true &&
    businessEventId &&
    participatingCompanyId &&
    businessEventRequirementId
  ) {
    navigate(`/businessEvent/${businessEventId}`, {
      state: {
        autoOpenRequirements: true,
        participatingCompanyId,
        businessEventRequirementId,
      },
    });
    return;
  }

  navigate(
    businessEventId ? `/businessEvent/${businessEventId}` : "/events",
  );
};

/**
 * Reports a user for inappropriate behavior.
 * @async
 * @param {Object} chat - Chat object where report originates
 * @param {Object} selectedUser - User being reported
 * @param {string} reason - Reason for reporting
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setChats - State setter for chats
 * @param {Function} setSelectedChat - State setter for selected chat
 * @param {Function} setSelectedUser - State setter for selected user
 * @returns {Promise<Object>} Result with success boolean and message
 */
export const reportUser = async (
  chat,
  selectedUser,
  reason,
  navigate,
  setChats,
  setSelectedChat,
  setSelectedUser,
) => {
  const reportingUserId = localStorage.getItem("userId");
  const requestBody = {
    chatId: chat.PK,
    reportingUserId,
    reportedUserId: selectedUser._id,
    reason,
  };
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(requestBody),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}websocket/report`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return {
          success: false,
          message: getGenericErrorMessage("SESSION_EXPIRED"),
        };
      }
      return await reportUser(
        chat,
        selectedUser,
        reason,
        navigate,
        setChats,
        setSelectedChat,
        setSelectedUser,
      );
    }
    logger.info("Report user response:", { data: data });
    await fetchUserChats(reportingUserId, navigate, setChats);
    setSelectedChat(null);
    setSelectedUser(null);
    return {
      success: true,
      message: getGenericErrorMessage("USER_REPORTED_SUCCESSFULLY"),
    };
  } catch (error) {
    logger.error("Error reporting user:", { err: sanitizeError(error) });
    return {
      success: false,
      message: getGenericErrorMessage("ERROR_REPORTING_USER"),
    };
  }
};

/**
 * Accepts a connection request and creates a new chat.
 * @async
 * @param {string} requestId - Connection request identifier
 * @param {Array} connectionRequests - Array of connection requests
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setConnectionRequests - State setter for requests
 * @param {Function} setChats - State setter for chats
 * @param {Function} [setChatParticipants] - Receives refreshed { users, companies }
 * @returns {Promise<void>}
 */
export const handleAcceptRequest = async (
  requestId,
  connectionRequests,
  navigate,
  setConnectionRequests,
  setChats,
  setChatParticipants,
) => {
  try {
    const approverUserId = localStorage.getItem("userId");
    const approverUserData = await getUser(approverUserId, navigate);

    const connectionRequest = connectionRequests.find(
      (req) => req._id === requestId,
    );

    if (!connectionRequest) {
      logger.error("Connection request not found");
      return;
    }

    // Fetch company data for approver
    let approverCompanyName = COMPANY_NOT_FOUND;
    if (approverUserData.RFC) {
      try {
        const approverCompanyData = await getCompanyByRFC(
          approverUserData.RFC,
          navigate,
        );
        approverCompanyName =
          approverCompanyData?.nombreComercial || COMPANY_NOT_FOUND;
      } catch (error) {
        logger.error(
          `Error fetching approver company data for RFC ${approverUserData.RFC}:`,
          { err: sanitizeError(error) },
        );
      }
    }

    const requestBody = {
      senderUserId: connectionRequest.senderUserId,
      approverUserId: approverUserId,
      approverCompanyName: approverCompanyName,
      approverCompanyRFC: approverUserData.RFC,
      approverUserName: approverUserData.nombre,
    };

    const result = await acceptConnectionRequest(
      requestId,
      requestBody,
      navigate,
    );
    logger.info("Result:", { data: result });
    if (result.statusCode === HTTP_CREATED) {
      setConnectionRequests((prev) =>
        prev.filter((req) => req._id !== requestId),
      );
      logger.info("Connection request accepted successfully");

      await fetchUserChats(
        approverUserId,
        navigate,
        setChats,
        setChatParticipants,
      );
      navigateAfterCustomerCompanyConnectionAccepted(
        connectionRequest,
        navigate,
      );
    } else {
      logger.error("Failed to accept connection request");
      throw new Error(getGenericErrorMessage("FAILED_TO_ACCEPT_CONNECTION_REQUEST"));
    }
  } catch (error) {
    logger.error("Error accepting connection request:", {
      err: sanitizeError(error),
    });
  }
};

/**
 * Rejects a connection request.
 * @async
 * @param {string} requestId - Connection request identifier
 * @param {Array} connectionRequests - Array of connection requests
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setConnectionRequests - State setter for requests
 * @returns {Promise<void>}
 */
export const handleRejectRequest = async (
  requestId,
  connectionRequests,
  navigate,
  setConnectionRequests,
) => {
  try {
    const rejectorUserId = localStorage.getItem("userId");
    const rejectorUserData = await getUser(rejectorUserId, navigate);

    const connectionRequest = connectionRequests.find(
      (req) => req._id === requestId,
    );

    if (!connectionRequest) {
      logger.error("Connection request not found");
      return;
    }

    // Fetch company data for rejector
    let recipientCompanyName = COMPANY_NOT_FOUND;
    if (rejectorUserData.RFC) {
      try {
        const rejectorCompanyData = await getCompanyByRFC(
          rejectorUserData.RFC,
          navigate,
        );
        recipientCompanyName =
          rejectorCompanyData?.nombreComercial || COMPANY_NOT_FOUND;
      } catch (error) {
        logger.error(
          `Error fetching rejector company data for RFC ${rejectorUserData.RFC}:`,
          { err: sanitizeError(error) },
        );
      }
    }

    const requestBody = {
      senderUserId: connectionRequest.senderUserId,
      recipientCompanyRFC: rejectorUserData.RFC,
      recipientCompanyName: recipientCompanyName,
    };

    const result = await rejectConnectionRequest(
      requestId,
      requestBody,
      navigate,
    );
    logger.info("Result:", { data: result });
    if (result.statusCode === HTTP_OK || result.statusCode === HTTP_CREATED) {
      setConnectionRequests((prev) =>
        prev.filter((req) => req._id !== requestId),
      );
      logger.info("Connection request rejected successfully");
    } else {
      logger.error("Failed to reject connection request");
    }
  } catch (error) {
    logger.error("Error rejecting connection request:", {
      err: sanitizeError(error),
    });
  }
};

/**
 * Navigates to view a company profile from a connection request.
 * @async
 * @param {Object} request - Connection request with RFC and senderUserId
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const handleViewProfile = async (request, navigate) => {
  try {
    const companyData = await getCompanyByRFC(
      request.senderUserId,
      request.RFC,
      navigate,
    );

    if (companyData) {
      navigate("/companyProfile", {
        state: {
          company: companyData,
          context: "chat",
        },
      });
    } else {
      logger.error("Company not found for RFC:", { data: request.RFC });
    }
  } catch (error) {
    logger.error("Error fetching company profile:", {
      err: sanitizeError(error),
    });
  }
};

/**
 * Rejects a business event connection request and updates appointment status.
 * @async
 * @param {Object} request - Connection request object
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setConnectionRequests - State setter for requests
 * @returns {Promise<void>}
 */
export const handleRejectBusinessEventConnectionRequest = async (
  request,
  navigate,
  setConnectionRequests,
) => {
  if (!request) {
    return;
  }
  try {
    // Update connection request status to rejected
    await updateConnectionRequest(
      request._id,
      { status: "rejected" },
      navigate,
    );

    // Update related appointment estadoCitaVirtual to rejected
    const eventId = request?.businessEvent?._id;
    const appointmentId = request?.appointmentId;
    if (eventId && appointmentId) {
      await updateAppointment(navigate, appointmentId, eventId, {
        estadoCitaVirtual: "rejected",
      });
    }
    setConnectionRequests((prev) =>
      prev.filter((req) => req._id !== request._id),
    );
  } catch (error) {
    logger.error("Error rejecting business event connection request:", {
      err: sanitizeError(error),
    });
  }
};

/**
 * Accepts a business event connection request and creates virtual meeting.
 * @async
 * @param {Object} request - Connection request with appointment data
 * @param {Function} navigate - React Router navigation function
 * @param {Function} t - Translation function
 * @param {Function} setIsAcceptButtonDisabled - State setter for button state
 * @param {Function} setConnectionRequests - State setter for requests
 * @returns {Promise<void>}
 */
export const handleAcceptBusinessEventConnectionRequest = async (
  request,
  navigate,
  t,
  setIsAcceptButtonDisabled,
  setConnectionRequests,
) => {
  if (!request) {
    return;
  }
  try {
    const eventId = request?.businessEvent?._id;
    const appointment = request?.appointment || {};
    const appointmentId = request?.appointmentId;
    const buyerCompanyName =
      request?.senderUserCompany?.nombreComercial || request?.empresa || "";

    const subject = t("pages.chat.virtualMeeting.subject", {
      company: buyerCompanyName,
    });

    const virtualMeetingData = {
      subject,
      startDate: appointment?.diaCitaVirtual,
      startTime: appointment?.horaInicioCitaVirtual,
      appointmentDuration: appointment?.duracionCitaVirtual,
      organizerUserId: appointment?.idUsuarioComprador,
      attendeeUserId: appointment?.idUsuarioRemitente,
      connectionRequestId: request._id,
    };

    const result = await createVirtualAppointment(
      navigate,
      eventId,
      appointmentId,
      virtualMeetingData,
    );

    // Check if the request was successful
    if (
      result &&
      (result.statusCode === HTTP_OK || result.statusCode === HTTP_CREATED)
    ) {
      // Remove the accepted request from the connection requests list
      if (setConnectionRequests) {
        setConnectionRequests((prev) =>
          prev.filter((req) => req._id !== request._id),
        );
      }
      logger.info("Business event connection request accepted successfully");
    } else {
      // If the result indicates failure, re-enable the button
      if (setIsAcceptButtonDisabled) {
        setIsAcceptButtonDisabled(false);
      }
      logger.error("Failed to accept business event connection request");
    }
  } catch (error) {
    logger.error("Error accepting business event connection request:", {
      err: sanitizeError(error),
    });
    // Re-enable the button on error
    if (setIsAcceptButtonDisabled) {
      setIsAcceptButtonDisabled(false);
    }
    throw error; // Re-throw to let the caller handle it
  }
};
